import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import styled from '@emotion/styled';
import {
  fontSize,
  fontWeight,
  spacing,
  mediaMax,
  borderRadius,
} from 'presentations/views/styles';
import {
  background,
  black,
  blueGrayPale,
  gray01,
  gray04,
  gray06,
  white,
} from 'presentations/views/styles/color';
import ProductFeatureContent from 'presentations/views/components/organisms/ProductFeatureContent';
import Image from 'presentations/views/components/atoms/Image';
import ResourcePageTemplate from 'presentations/views/components/templates/ResourcePageTemplate';
import { FeatureItem } from 'presentations/api/schema/contents';
import { useFeaturePage } from './hooks';
import ResourceShareList from 'presentations/views/components/organisms/ResourceShareList';
import RelatedFeatureListItem from 'presentations/views/components/molecules/RelatedFeatureListItem';
import Link from 'presentations/views/components/atoms/Link';
import Icon from 'presentations/views/components/atoms/Icon';
import Headline from 'presentations/views/components/atoms/Headline';
import Head from 'next/head';

import dynamic from 'next/dynamic';
import { SyntheticEvent, useCallback, useMemo } from 'react';
import {
  featurePath,
  helpPath,
  pricingPath,
  webPath,
} from 'presentations/routes/path';
import { useRouter } from 'next/router';
import { stripHtmlTag } from 'presentations/views/helpers/formatter';
import { transformContentfulImageUrl } from 'presentations/assets';
const Header = dynamic(() =>
  import('presentations/views/components/organisms/Header'),
);
const Breadcrumbs = dynamic(() =>
  import('presentations/views/components/organisms/Breadcrumbs'),
);

interface Props {
  feature: FeatureItem;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const { state, actions } = useFeaturePage({
    feature: props.feature,
  });
  const router = useRouter();

  const shareUrl = webPath(featurePath(props.feature.slug), router.locale);

  const onClickTC = useCallback((e: SyntheticEvent<HTMLLIElement>) => {
    const index = e.currentTarget.dataset.id;
    if (!index) return;
    const targets = document.querySelectorAll(
      '#js-feature-body h2, #js-feature-body h3',
    );
    if (targets && targets.length > 0) {
      const target = targets[Number(index)];
      window.location.hash = `#tc${index}`;
      target.scrollIntoView();
    }
  }, []);

  const planNames = props.feature.plans.map((p) => p.name).join(' / ');
  const addedToc = useMemo(() => {
    const toc = [];
    if (props.feature.answers.length > 0) {
      toc.push(t('answers'));
    }
    if (props.feature.integrations.length > 0) {
      toc.push(t('integrations'));
    }
    if (props.feature.relatedFeatures.length > 0) {
      toc.push(t('related features'));
    }
    return toc;
  }, []);

  const descToc = props.feature.tableOfContents || [];
  const tableOfContents = [...descToc, ...addedToc];

  return (
    <ResourcePageTemplate
      breadcrumbs={<Breadcrumbs routes={state.routes} />}
      header={<Header viewer={state.viewer} />}
    >
      <>
        <Head>
          {props.feature.thumbnailUrl && (
            <link rel="preload" href={props.feature.thumbnailUrl} as="image" />
          )}
        </Head>
        <HeaderContainer isCoverImage={!!props.feature.thumbnailUrl}>
          <TitleContainer>
            <Title>{props.feature.name}</Title>
            <Summary>{props.feature.summary}</Summary>
            <PlanLink href={pricingPath()}>
              <PlanIcon name="trophy-variant" />
              <PlanTitle>{t('plan')}</PlanTitle>
              {planNames}
            </PlanLink>
          </TitleContainer>
          {!!props.feature.thumbnailUrl && (
            <HeaderImageContainer>
              <HeaderImage
                src={transformContentfulImageUrl(props.feature.thumbnailUrl, {
                  width: 672,
                  height: 448,
                  dpr: 2,
                })}
                alt={props.feature.name}
                width={672}
                height={448}
                dpr={2}
              />
            </HeaderImageContainer>
          )}
        </HeaderContainer>
        <Container>
          <MainContent>
            <ContentWrapper>
              <ContentContainer>
                {tableOfContents.length > 0 && (
                  <TableOfContentsContainer>
                    <TableOfContentsTitle>
                      {t('table_of_contents')}
                    </TableOfContentsTitle>
                    <TableOfContentsList>
                      {tableOfContents.map((item, index) => (
                        <TableOfContentsListItem
                          key={item}
                          data-id={index}
                          onClick={onClickTC}
                          tagName={item.tagName}
                        >
                          {item.text}
                        </TableOfContentsListItem>
                      ))}
                    </TableOfContentsList>
                  </TableOfContentsContainer>
                )}
                <div id="js-feature-body">
                  <ProductFeatureContent content={props.feature.body} />
                  {props.feature.answers.length > 0 && (
                    <>
                      <ListTitle variant="h2">{t('answers')}</ListTitle>
                      <AnswerList>
                        {props.feature.answers.map((a, index) => (
                          <AnswerListItem key={index}>
                            <AnswerQuestion variant="h3">
                              Q.{' '}
                              <Link href={helpPath(a.slug)}>{a.question}</Link>
                            </AnswerQuestion>
                            <AnswerDescriptionContainer>
                              <AnswerPrefix>A.</AnswerPrefix>
                              <AnswerDescriptionLink href={helpPath(a.slug)}>
                                <AnswerDescription
                                  dangerouslySetInnerHTML={{
                                    __html: a.description,
                                  }}
                                />
                              </AnswerDescriptionLink>
                            </AnswerDescriptionContainer>
                          </AnswerListItem>
                        ))}
                      </AnswerList>
                    </>
                  )}
                  {props.feature.relatedFeatures.length > 0 && (
                    <>
                      <ListTitle variant="h2">
                        {t('related features')}
                      </ListTitle>
                      <RelatedFeatureList>
                        {props.feature.relatedFeatures.map((f) => (
                          <RelatedFeatureListItemContainer>
                            <RelatedFeatureListItem feature={f} />
                          </RelatedFeatureListItemContainer>
                        ))}
                      </RelatedFeatureList>
                    </>
                  )}
                  {props.feature.integrations.length > 0 && (
                    <>
                      <ListTitle variant="h2">{t('integrations')}</ListTitle>
                      <IntegrationList>
                        {props.feature.integrations.map((i, index) => (
                          <IntegrationListItem key={index}>
                            <Image src={i.image} alt={i.name} width={48} />
                            <IntegrationName>{i.name}</IntegrationName>
                          </IntegrationListItem>
                        ))}
                      </IntegrationList>
                    </>
                  )}
                </div>
              </ContentContainer>
              <SideShareIconContainer>
                <SideShareIconStickyWrapper>
                  <SideShareIconSticky>
                    <ResourceShareList
                      shareTitle={props.feature.name}
                      shareUrl={shareUrl}
                    />
                  </SideShareIconSticky>
                </SideShareIconStickyWrapper>
              </SideShareIconContainer>
            </ContentWrapper>
          </MainContent>
        </Container>
      </>
    </ResourcePageTemplate>
  );
}

const AnswerList = styled.div`
  /* display: flex;
  flex-wrap: wrap;
  justify-content: space-between; */
  margin-bottom: ${spacing(48)};
  ${mediaMax.tablet} {
    margin-bottom: ${spacing(24)};
  }
`;
const AnswerListItem = styled.div`
  margin-bottom: ${spacing(16)};
  :last-of-type {
    margin-bottom: 0;
  }
`;
const AnswerPrefix = styled.span`
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(18)};
  margin-right: ${spacing(8)};
`;
const AnswerQuestion = styled(Headline)`
  margin-bottom: ${spacing(16)};
  a {
    color: ${black};
  }
`;
const AnswerDescriptionContainer = styled.div`
  display: flex;
  padding: ${spacing(16)};
  border-radius: ${borderRadius(16)};
`;
const AnswerDescription = styled.div`
  flex: 1;
`;
const AnswerDescriptionLink = styled(Link)`
  color: ${black};
`;
const ListTitle = styled(Headline)`
  margin-bottom: ${spacing(32)};
  font-size: ${fontSize(28)};

  ${mediaMax.tablet} {
    margin: 0 0 ${spacing(20)} 0;
    padding: 0 0 ${spacing(12)} 0;
    border-bottom: 1px solid ${gray01};
    font-size: ${fontSize(24)};
  }
`;
const RelatedFeatureList = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  margin-bottom: ${spacing(48)};
  ${mediaMax.tablet} {
    margin-bottom: ${spacing(24)};
  }
`;
const RelatedFeatureListItemContainer = styled.div`
  margin-bottom: ${spacing(16)};
  ${mediaMax.tablet} {
  }
`;

const IntegrationList = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
`;
const IntegrationListItem = styled.div`
  border-radius: ${borderRadius(16)};
  border: 1px solid ${gray04};
  padding: ${spacing(12)};
  width: 300px;
  display: flex;
  align-items: center;
  margin-bottom: ${spacing(16)};
`;
const IntegrationName = styled.span`
  font-weight: ${fontWeight('bold')};
  margin-left: ${spacing(8)};
`;

const Container = styled.div`
  letter-spacing: 0.05em;
  display: flex;
  justify-content: center;
  ${mediaMax.desktop} {
    display: block;
  }
`;
const MainContent = styled.div`
  width: 672px;
  ${mediaMax.desktop} {
    margin: 0 auto;
  }
  ${mediaMax.tablet} {
    width: 100%;
    padding: 0 ${spacing(12)};
    margin: 0;
  }
`;

const ContentWrapper = styled.div`
  position: relative;
`;

const ContentContainer = styled.div`
  margin-bottom: ${spacing(64)};
  ${mediaMax.tablet} {
    margin-bottom: ${spacing(32)};
  }
`;

const HeaderContainer = styled.div<{ isCoverImage: boolean }>`
  position: relative;
  height: 648px;
  height: ${(props) => (props.isCoverImage ? '648px' : 'auto')};
  ${mediaMax.tablet} {
    height: ${(props) => (props.isCoverImage ? '420px' : 'auto')};
  }
`;
const HeaderImageContainer = styled.div`
  width: 672px;
  height: 448px;
  margin: 0 auto;
  padding: 16px;
  background-color: ${gray04};
  border-radius: 16px;

  position: absolute;
  top: 220px;
  left: 0;
  right: 0;

  ${mediaMax.tablet} {
    /* position: static; */
    top: 140px;
    width: calc(100% - 24px);
    left: 12px;
    right: 12px;
    height: auto;
    margin: ${spacing(20)} 0 0;
  }
`;

const HeaderImage = styled(Image)`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: ${borderRadius()};
`;

const TitleContainer = styled.div`
  background-color: ${black};
  color: ${white};
  height: 280px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;

  ${mediaMax.tablet} {
    padding: ${spacing(24)} ${spacing(12)};
    height: 220px;
  }
`;
const Title = styled.h1`
  font-size: ${fontSize(36)};
  font-weight: ${fontWeight('bold')};
  line-height: 1.4;
  letter-spacing: 0.05em;
  overflow-wrap: break-word;
  padding-top: ${spacing(48)};
  max-width: 672px;

  ${mediaMax.tablet} {
    font-size: ${fontSize(20)};
    padding-top: 0;
  }
`;
const Summary = styled.p`
  margin-top: ${spacing(16)};
  max-width: 672px;
  ${mediaMax.tablet} {
    font-size: ${fontSize(13)};
  }
`;

const PlanLink = styled(Link)`
  margin-top: ${spacing(8)};
  font-size: ${fontSize(13)};
  color: ${white};
  display: flex;
  align-items: center;
`;
const PlanIcon = styled(Icon)`
  color: ${white};
  width: 14px !important;
  height: 14px !important;
`;
const PlanTitle = styled.span`
  font-weight: ${fontWeight('bold')};
  margin-left: ${spacing(4)};
  margin-right: ${spacing(8)};
`;

const TableOfContentsContainer = styled.div`
  margin: ${spacing(32)} 0 0 0;
  padding: ${spacing(20)} ${spacing(40)} ${spacing(28)} ${spacing(40)};

  background-color: ${gray04};
  border-radius: ${borderRadius()};

  ${mediaMax.tablet} {
    margin: ${spacing(20)} 0 0 0;
    padding: ${spacing(12)} ${spacing(20)} ${spacing(12)} ${spacing(20)};
  }
`;

const TableOfContentsTitle = styled.div`
  margin-bottom: ${spacing(12)};
  font-size: ${fontSize(14)};
  line-height: 160%;

  ${mediaMax.tablet} {
    margin-bottom: ${spacing(8)};
    font-size: ${fontSize(11)};
  }
`;

const TableOfContentsList = styled.ul`
  padding: 0;
`;

const TableOfContentsListItem = styled.li<{ tagName?: string }>`
  margin-bottom: ${spacing(4)};
  padding-left: ${(props) => props.tagName === 'H3' && spacing(16)};
  cursor: pointer;

  &::before {
    content: '- ';
  }
  :last-of-type {
    margin-bottom: 0;
  }

  ${mediaMax.tablet} {
    font-size: ${fontSize(11)};
  }
`;

const SideShareIconContainer = styled.div`
  position: absolute;
  top: 20px;
  left: -64px;
  height: 100%;

  ${mediaMax.tablet} {
    display: none;
  }
`;

const SideShareIconStickyWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const SideShareIconSticky = styled.div`
  position: sticky;
  top: 96px;
  left: 0;
  height: auto;
`;
